import { conversationConstants } from './constants';

/** The record type the BE stamps on a conversation written by an internal email. */
export const INTERNAL_EMAIL_RECORD_TYPE = 'AtsUserMessage';

interface ConversationUser {
  id?: number | string;
  name?: string;
  avatar?: string | null;
  email?: string;
}

/** A pending internal email, as `scheduled_candidate_messages` returns it. */
export interface ScheduledInternalEmail {
  id: number;
  stage?: string;
  scheduled_at?: string | null;
  timezone?: string | null;
  subject?: string;
  body?: string;
  sender?: ConversationUser;
  to?: ConversationUser[];
  cc?: ConversationUser[];
  bcc?: ConversationUser[];
  ats_job_stage?: { id?: number; name?: string };
}

/** True for a conversation row that an internal email produced. */
export const isInternalEmailConversation = (conversation?: {
  record?: { type?: string | null } | null;
}) => conversation?.record?.type === INTERNAL_EMAIL_RECORD_TYPE;

/**
 * Maps a pending internal email onto the shape the Scheduled section renders.
 *
 * Internal emails are a different record on the backend — one row per teammate —
 * so they arrive under their own key rather than inside the candidate-message
 * list. Reshaping them here lets the existing row render them, and keeps the
 * section a single ordered list rather than two.
 *
 * `repliable` is false by construction: the in-app reply writes to the
 * candidate, and this email never went to them.
 */
export const toScheduledConversation = (email: ScheduledInternalEmail) => {
  const [primaryRecipient, ...otherRecipients] = email?.to ?? [];

  return {
    id: email?.id,
    // One row per email, so the extra recipients ride along as cc for counting.
    recipient: primaryRecipient ?? null,
    sender: email?.sender ?? null,
    cc: [...otherRecipients, ...(email?.cc ?? [])],
    bcc: email?.bcc ?? [],
    subject: email?.subject ?? '',
    body: email?.body ?? '',
    scheduled_at: email?.scheduled_at ?? null,
    sent_at: null,
    timezone: email?.timezone ?? null,
    status: email?.stage ?? 'scheduled',
    channel: conversationConstants.email.id,
    attachments: [],
    repliable: false,
    record: { type: INTERNAL_EMAIL_RECORD_TYPE, id: email?.id },
    ats_job_stage: email?.ats_job_stage ?? null,
  };
};

/**
 * Folds pending internal emails into the scheduled list, ordered with it so the
 * section reads as one timeline.
 */
export const withScheduledInternalEmails = (
  scheduledMessages: Record<string, any>[] = [],
  internalEmails: ScheduledInternalEmail[] = []
) =>
  [...scheduledMessages, ...internalEmails.map(toScheduledConversation)].sort(
    (a, b) =>
      new Date(a?.scheduled_at ?? 0).getTime() -
      new Date(b?.scheduled_at ?? 0).getTime()
  );
